import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { createTrackedSelector } from 'react-tracked';

export interface CacheStore<T = unknown> {
  cache: Record<string, T>;
  set: (key: string, value: T) => void;
  get: (key: string) => T | undefined;
  remove: (key: string) => void;
  removeWithPredicate: (predicate: (key: string) => boolean) => void;
  clear: () => void;
}

export const cacheStore = create<CacheStore>()(
  devtools((set, get) => ({
    cache: {},
    get: (key) => get().cache[key],
    set: (key, value) => {
      set(
        {
          cache: {
            ...get().cache,
            [key]: value,
          },
        },
        undefined,
        `cacheStore/set/${key}`,
      );
    },
    remove: (key) => {
      const { [key]: _, ...rest } = get().cache;
      set(
        {
          cache: rest,
        },
        undefined,
        `cacheStore/remove/${key}`,
      );
    },
    removeWithPredicate: (predicate) => {
      const newCache: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(get().cache)) {
        if (!predicate(key)) {
          newCache[key] = value;
        }
      }
      set({ cache: newCache }, undefined, `cacheStore/removeWithPredicate`);
    },
    clear: () => set({ cache: {} }, undefined, `cacheStore/clear`),
  })),
);

export const useCacheStore = createTrackedSelector(cacheStore);
